import { useCallback, useRef, useState } from 'react';
import { readDataStream } from './use-chat';
import { parseAPIError, ParsedError } from './error-utils';

// Type definitions for useCompletion - Generic streaming API hook
interface UseCompletionOptions {
  api: string;
  body?: Record<string, any>;
  onFinish?: (prompt: string, completion: string, payloads?: Record<string, any>) => void;
  onError?: (error: ParsedError) => void;
}

interface UseCompletionReturn {
  completion: string;
  setCompletion: (completion: string) => void;
  loading: boolean;
  complete: (prompt: string, payloads?: Record<string, any>) => Promise<void>;
  error: ParsedError | null;
  setError: (error: ParsedError | null) => void;
  isGenerating: boolean;
}

// Store last request timestamps by API endpoint
const lastRequestTimestamp: Record<string, number> = {};

export const useCompletion = ({
  api,
  body = {},
  onFinish,
  onError,
}: UseCompletionOptions): UseCompletionReturn => {
  const [completion, setCompletion] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [prompt, setPrompt] = useState<string>('');
  const [error, setError] = useState<ParsedError | null>(null);

  const handleStreamResponse = useCallback(
    async (response: Response, payloads: Record<string, any> = {}) => {
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Response body is not readable');
      }

      const streamGenerator = readDataStream(reader, {
        isAborted: () => abortControllerRef.current?.signal.aborted ?? false,
      });
      let chunks = '';

      try {
        for await (const part of streamGenerator) {
          switch (part.type) {
            case 'text':
              chunks += part.value;
              setCompletion(chunks);
              break;
          }
        }

        if (onFinish) {
          onFinish(prompt, chunks, payloads);
        }
      } catch (e) {
        const parsedError = parseAPIError(e);
        onError?.(parsedError);
        setError(parsedError);
      }
    },
    [onFinish, onError, prompt]
  );

  const complete = useCallback(
    async (inputPrompt: string, payloads: Record<string, any> = {}): Promise<void> => {
      // Check if enough time has passed (1 second) since the last request
      const currentTimestamp = Date.now();
      if (currentTimestamp - (lastRequestTimestamp[api] || 0) < 1000) {
        return;
      }

      lastRequestTimestamp[api] = currentTimestamp;

      setPrompt(inputPrompt);
      setError(null);
      setLoading(true);

      const payload = { ...body, prompt: inputPrompt, ...payloads };

      try {
        const response = await fetch(api, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(payloads.headers || {}),
          },
          body: JSON.stringify(payload),
        });

        if (response.ok) {
          setIsGenerating(true);
          await handleStreamResponse(response, payload);
        } else {
          const errorData = await response.json().catch(() => {
            return {
              message: `Request failed with status ${response.status}`,
            };
          });
          const parsedError = parseAPIError(errorData);
          setError(parsedError);
          onError?.(parsedError);
        }
      } catch (err) {
        const parsedError = parseAPIError(err);
        onError?.(parsedError);
        setError(parsedError);
      } finally {
        setLoading(false);
        setIsGenerating(false);
      }
    },
    [api, body, handleStreamResponse, onError]
  );

  return {
    completion,
    setCompletion,
    loading,
    complete,
    error,
    setError,
    isGenerating,
  };
};
